import React, { useState, useEffect } from "react";
import { Terminal, ShieldAlert, Activity } from "lucide-react";

const LOADING_STEPS = [
  { threshold: 15, text: "CONNECTING TO TACTICAL NETWORK..." },
  { threshold: 35, text: "SCANNING REGIONAL DISTRICTS..." },
  { threshold: 55, text: "SYNCING CRYPTO-LEDGER..." },
  { threshold: 75, text: "ESTABLISHING PROXY NODES..." },
  { threshold: 90, text: "BYPASSING LOCAL FIREWALLS..." },
  { threshold: 100, text: "ACCESS GRANTED. WELCOME TO TRAP CITY." },
];

export default function SplashScreen({ onComplete }) {
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("INITIALIZING SECURE UPLINK...");
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((previous) =>
        Math.min(previous + Math.random() * 12, 100),
      );
    }, 80);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const step = LOADING_STEPS.find(({ threshold }) => progress <= threshold);
    if (step) setStatus(step.text);
    if (progress < 100) return undefined;

    const fadeTimer = setTimeout(() => setFadeOut(true), 300);
    const completionTimer = setTimeout(onComplete, 800);
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(completionTimer);
    };
  }, [onComplete, progress]);

  return (
    <div
      className={`fixed inset-0 z-[9999] bg-black flex flex-col items-center justify-center font-mono transition-opacity duration-700 ${fadeOut ? "opacity-0" : "opacity-100"}`}
    >
      {/* Background Scanning Effect */}
      <div className="absolute inset-0 pointer-events-none opacity-20 bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.25)_50%),linear-gradient(90deg,rgba(255,0,0,0.06),rgba(0,255,0,0.02),rgba(0,0,255,0.06))] z-10 bg-[length:100%_2px,3px_100%]" />

      {/* Thematic Overlays */}
      <img
        src="/assets/textures/thug_grp4.png"
        alt="Thugs"
        className="absolute left-0 bottom-0 h-[30%] md:h-[35%] sm:h-[60%] object-contain opacity-20 z-0 pointer-events-none select-none grayscale"
      />
      <img
        src="/assets/textures/police_grp4.png"
        alt="Police"
        className="absolute right-0 bottom-0 h-[30%] md:h-[35%] sm:h-[60%] object-contain opacity-20 z-0 pointer-events-none select-none grayscale hidden lg:block"
      />

      <div className="relative z-20 flex flex-col items-center">
        {/* Brand Logo Section */}
        <div className="mb-8 text-center">
          <h1 className="text-7xl md:text-9xl font-black italic text-white tracking-tighter uppercase">
            Trap
            <span className="text-green-500 drop-shadow-[0_0_15px_rgba(34,197,94,0.8)]">
              City
            </span>
          </h1>
          <div className="flex items-center justify-center gap-4 mt-2">
            <ShieldAlert className="text-red-500 animate-pulse" size={24} />
            <span className="text-zinc-500 text-xs font-bold uppercase tracking-[0.4em]">
              Tactical Intelligence Network
            </span>
          </div>
        </div>

        {/* Loading UI */}
        <div className="w-64 md:w-96">
          <div className="flex justify-between text-[10px] text-zinc-400 mb-2 font-bold uppercase">
            <span className="flex items-center gap-1">
              <Activity size={10} className="text-green-500" /> Uplink: Active
            </span>
            <span className="text-green-500">{Math.round(progress)}%</span>
          </div>

          {/* Progress Bar Container - Higher contrast border and background */}
          <div className="h-2 w-full bg-zinc-800 border border-zinc-700 shadow-inner overflow-hidden">
            <div
              className="h-full bg-green-500 transition-all duration-150 ease-out shadow-[0_0_15px_rgba(34,197,94,0.6)]"
              style={{ width: `${progress}%`, backgroundColor: "#48bb78" }} // Forced inline color to ensure visibility
            />
          </div>

          {/* Scrolling Status Text */}
          <div className="mt-4 h-6 flex items-center justify-center gap-2">
            <Terminal size={14} className="text-zinc-600" />
            <span className="text-zinc-300 text-[10px] uppercase font-bold tracking-widest">
              {status}
            </span>
          </div>
        </div>
      </div>

      {/* Static corner details */}
      <div className="absolute top-8 left-8 text-zinc-800 text-[10px] uppercase leading-tight font-bold hidden md:block">
        v6.4.2 // optimized_build
        <br />
        sector_7_access_active
        <br />
        node_id: TC-GLOBAL
      </div>
    </div>
  );
}
